/**
 * Every date this app prints: one spelling family, and never in the browser's zone.
 *
 * ## The zone
 *
 * A browser formatter with no `timeZone` reads its value on the *laptop's* clock. That is right for
 * nothing this app stores (INV-UTC: stored in UTC, judged in the account's zone), and it was the
 * default at fourteen call sites. Eight personas hit it independently in one round, with the
 * browser in Pacific/Kiritimati: a task the API planned for `2026-08-21` read *planned Aug 22*
 * under a Today band that counted the same day correctly, a project's *Sep 1 → Sep 20* read
 * *Sep 2 → Sep 21* beside a countdown that was right, and a thread line written at 20:35 in Warsaw
 * read *8/23/2026, 8:35:47 AM* — the next morning.
 *
 * The two kinds of value need two different answers, which is why there are two helpers:
 *
 *  - **A calendar day** (`scheduledDate`, `trackedSince`, a project's `startDate`, a goal's
 *    `targetDate`) has no zone at all. It is formatted at noon UTC *in* UTC, so no clock anywhere
 *    can move it.
 *  - **An instant** (`createdAt`, `dueAt`, a session's `lastSeenAt`) is a moment, and which day and
 *    hour it was is a question about a zone. The zone is the **account's** — `useAccountDay()`
 *    hands it out — not the browser's. A travelling laptop does not change which day a comment was
 *    written on.
 *
 * ## The spelling (`01a0809a-3b90`)
 *
 * One account printed the same stored day five ways: *2026-08-21* in a search pill, *planned
 * Aug 21* in the list, *08/22/2026* in the panel, *2026-09-07 → 2026-09-13* on Reflection and
 * *Captured 8/21/2026* in a tooltip — because every call site passed its own `Intl` options and
 * its own locale (`'en-GB'` at six, the browser's at the rest). So neither is an argument any more.
 * A caller picks a **style** by name and this file decides what it looks like:
 *
 *  - `day` — *20 Oct* in the current year, *20 Oct 2025* otherwise. The year is printed only when
 *    it is needed to tell which October, so twelve weekly labels this year carry none.
 *  - `weekday` — *Tue 20 Oct*, with the same year rule, where the day of the week is the point.
 *  - `month` — *Oct*, for a column of a month grid.
 *  - a range (`readableRange`) — *19–25 Oct*, *28 Sept – 3 Oct*, and a single day as that day
 *    rather than *X to X* (`01a03813-9a63`, absorbed).
 *  - a clock (`readableInstant(…, 'clock')`) — *14:05*, 24-hour, which is what Today's timeline
 *    and the day page already drew.
 *
 * ISO strings are for machines: exports, the API, MCP. Nothing a person reads is spelled that way.
 *
 * **Locale.** `DISPLAY_LOCALE`, below, is the one place a locale is named in `apps/web/src`;
 * `dateLabelsAreOneFamily.spec.ts` fails if another file names one or calls a bare
 * `toLocaleDateString`. See the constant for why it is fixed rather than the browser's.
 *
 * **"The current year"** is read off the account's clock for an instant and off UTC for a calendar
 * day, which has no zone to read it in. That decides only whether a year is *printed*, never which
 * day is: for the hours around New Year when UTC and the account disagree, a day may carry a year
 * it did not need, or lack one for an evening. Threading the account's today into every call to
 * save that was judged not worth fifty call sites.
 *
 * Two formatters in `apps/web/src` are deliberately not routed through here: `Now.tsx`'s timeline
 * clock is the laptop's on purpose, a separate question nobody has measured; and `Reflection.tsx`'s
 * month heading (*October 2026*) is a name of a month, not a date.
 */

/**
 * The family's one locale — fixed, not the browser's.
 *
 * Set by `planner-architect` on `01a0809a-3b90` (comment `01a15d18-0b80`), superseding "the
 * browser's locale everywhere". The words around every date here are English and
 * there is no translation, so the browser's locale would put *20 paź* inside *promised …,
 * overdue*; and the server already writes day-month English into stored text a reader sees
 * beside these labels — the waiting-on thread's *since 24 Aug* (`shortDay` in
 * `services/waitingOn.ts`), which cannot follow a viewer's locale because it is written once.
 * Following the browser would have put *since Aug 24* in the banner above that thread. `apps/web`
 * does not import `@planner/core`, so this literal and `shortDay`'s are two, each naming the other.
 *
 * Reopen it when an account-level language setting lands: the locale then becomes a per-account
 * read here *and* in `shortDay`, since stored text must follow it too.
 */
const DISPLAY_LOCALE = 'en-GB';

/** A day as written, `YYYY-MM-DD`, read at noon UTC so no offset can tip it into a neighbour. */
const noonUtc = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`);

/*
  One formatter per shape, built once.

  `toLocaleDateString` builds a fresh `Intl.DateTimeFormat` on every call — ~81µs against ~1.3µs for
  a reused one (measured in Phase 86, `Week.tsx`) — and a task list formats a date per row.
*/
const formatters = new Map<string, Intl.DateTimeFormat>();
const formatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(DISPLAY_LOCALE, options);
    formatters.set(key, f);
  }
  return f;
};

const yearIn = (at: Date, timeZone: string) =>
  Number(formatter({ year: 'numeric', timeZone }).format(at));

/** The date-shaped options for a style, with the year only when it is not the current one. */
const dateOptions = (style: 'day' | 'weekday', needsYear: boolean): Intl.DateTimeFormatOptions => ({
  ...(style === 'weekday' ? { weekday: 'short' } : {}),
  day: 'numeric',
  month: 'short',
  ...(needsYear ? { year: 'numeric' } : {}),
});

/*
  en-GB's weekday form is "Tue, 20 Oct" — `Intl` puts a comma after the weekday, which nothing else
  in this app's prose does. The family is "Tue 20 Oct", so it comes out here, once.
*/
const dropWeekdayComma = (s: string) => s.replace(/^(\p{L}+),/u, '$1');

export type DayStyle = 'day' | 'weekday' | 'month';

/**
 * A calendar day — `'2026-08-21'` → *21 Aug*, in every browser zone.
 *
 * Accepts a full ISO instant too and keeps only its date, for the one value stored as a midnight-UTC
 * instant that *means* a calendar day: a goal's `targetDate`. Read as an instant it was the 11th in
 * Pago Pago and the 12th in Kiritimati; read as the day it was entered as, it is the 12th.
 */
export const readableDay = (iso: string, style: DayStyle = 'day'): string => {
  const at = noonUtc(iso);
  if (style === 'month') return formatter({ month: 'short', timeZone: 'UTC' }).format(at);
  const needsYear = at.getUTCFullYear() !== new Date().getUTCFullYear();
  return dropWeekdayComma(
    formatter({ ...dateOptions(style, needsYear), timeZone: 'UTC' }).format(at),
  );
};

/**
 * Two calendar days as one span — *19–25 Oct*, *28 Sept – 3 Oct*, *30 Dec 2025 – 4 Jan 2026*.
 *
 * `formatRange` rather than two `readableDay`s joined by an arrow, because it drops whatever the
 * two ends share: the month when both are in it, and the whole thing when they are the same day.
 * That last case is the *2026-08-31 to 2026-08-31* Reflection printed for a period one day old.
 */
export const readableRange = (start: string, end: string): string => {
  const from = noonUtc(start);
  const to = noonUtc(end);
  const thisYear = new Date().getUTCFullYear();
  const needsYear = from.getUTCFullYear() !== thisYear || to.getUTCFullYear() !== thisYear;
  /*
    The dash is spelled here, not by ICU, because ICU versions disagree about it. Node 22's prints
    *19–25 Oct*; the Chromium in the CI image prints *19\u2009–\u200925 Oct*, thin spaces and all
    (measured in `mcr.microsoft.com/playwright:v1.62.1-noble`), so the same day read two ways by
    the two halves of this repo's own tests. The rule, once: tight between two bare day numbers
    (*19–25 Oct*), a spaced en dash wherever either side carries a month (*28 Sept – 3 Oct*).
  */
  const [left, right] = formatter({ ...dateOptions('day', needsYear), timeZone: 'UTC' })
    .formatRange(from, to)
    .split(/\s*–\s*/);
  if (right === undefined) return left!;
  return /^\d+$/.test(left!) ? `${left}–${right}` : `${left} – ${right}`;
};

export type InstantStyle = 'day' | 'weekday' | 'dayAndTime' | 'weekdayAndTime' | 'clock';

/**
 * An instant as the account's clock read it — `timezone` is `useAccountDay().timezone`.
 *
 * `day` prints only the date, which is then the day the instant fell on *in the account's zone*:
 * a deadline at 23:30 Warsaw time is that day's, not the next one's because a laptop sits further
 * east. `dayAndTime` (the default) is *20 Oct, 14:05*; `clock` is the time alone.
 */
export const readableInstant = (
  iso: string,
  timezone: string,
  style: InstantStyle = 'dayAndTime',
): string => {
  const at = new Date(iso);
  const clock: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  };
  if (style === 'clock') return formatter(clock).format(at);
  const needsYear = yearIn(at, timezone) !== yearIn(new Date(), timezone);
  const dateStyle = style === 'weekday' || style === 'weekdayAndTime' ? 'weekday' : 'day';
  const date = dropWeekdayComma(
    formatter({ ...dateOptions(dateStyle, needsYear), timeZone: timezone }).format(at),
  );
  if (style === 'day' || style === 'weekday') return date;
  return `${date}, ${formatter(clock).format(at)}`;
};
